import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';

const easeOut = [0.16, 1, 0.3, 1];
export const CONTACT_EMAIL = 'glenn.davis@discx-us.com';
export const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Inquiry from the DiSCX-US website')}`;

const focusAreas = [
  { icon: 'tabler:briefcase', title: 'Defense Acquisition', desc: 'How capability is planned, contracted, managed and fielded, from requirement to sustainment.' },
  { icon: 'tabler:truck-delivery', title: 'Logistics & Sustainment', desc: 'Keeping forces ready: supply chains, depots, maintenance and life-cycle support.' },
  { icon: 'tabler:star', title: 'Senior Leader Development', desc: 'Strategic leadership for general and flag officers, commanders and senior civilians.' },
  { icon: 'tabler:world', title: 'International Partnership', desc: 'Security cooperation and education that strengthen allied and partner defense organizations.' },
];

const credentials = [
  { logo: '/logos/education/harvard-jfk-schoolofgov-shield.png', text: 'Harvard University, John F. Kennedy School of Government' },
  { logo: '/logos/education/georgetown-flag-seal.png', text: 'Georgetown University, Government Affairs Institute' },
  { logo: '/logos/military/usairforce.png', text: 'United States Air Force, 37 years of service' },
];

const heroImages = ['/images/military-drone.jpg', '/images/army-helicopters.jpg', '/images/naval-destroyer.png', '/images/rsaf-tailwings.png'];

export default function LandingPage() {
  return (
    <div>
      {/* ==================== HERO ==================== */}
      <section className="relative flex min-h-[640px] items-center overflow-hidden bg-[#0a1628] md:min-h-[720px]">
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
          {heroImages.map((src, i) => (
            <motion.div key={src} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.2 + i * 0.3, ease: easeOut }} className="relative overflow-hidden">
              <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1.2, ease: easeOut }} className="absolute inset-0 bg-[#0a1628]/80" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.4, ease: easeOut }} className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-[#93c5fd]">
              Defense Industries Services &amp; Exchange
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.6, ease: easeOut }} className="mb-5 text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              Warfighting and Defense Education, Led by Experience
            </motion.h1>
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.8, ease: easeOut }} className="mb-6 text-xl font-semibold text-[#93c5fd] md:text-2xl">
              Acquisition • Logistics &amp; Sustainment • Senior Leadership
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 2.0, ease: easeOut }} className="mx-auto mb-10 max-w-3xl text-base leading-relaxed text-white/90 md:text-lg">
              DiSCX-US educates military and civilian leaders on the disciplines that decide readiness: how defense capability is planned, acquired, fielded and sustained, taught by people who have done the work.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 2.2, ease: easeOut }} className="flex flex-col justify-center gap-4 sm:flex-row">
              <a href={MAILTO} className="btn-sweep bg-[#1a365d] px-8 py-4 text-lg font-semibold">
                Contact Us
                <Icon icon="mdi:arrow-right" className="h-5 w-5" />
              </a>
              <a href="#leadership" className="btn-sweep-outline border-2 border-white/40 px-8 py-4 text-lg font-semibold">
                Our Leadership
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== ABOUT ==================== */}
      <section id="about" className="scroll-mt-16 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-[#1a365d] md:text-4xl">About DiSCX-US</h2>
            <p className="text-lg leading-relaxed text-[#4a5568]">
              DiSCX-US was formed to provide education on the key warfighting and defense disciplines that modern forces depend on. Our programs draw on decades of command, acquisition and sustainment experience to help defense organizations and their leaders build the judgment their missions require.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {focusAreas.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="feature-card h-full rounded-xl border border-navy-100 bg-white p-6 shadow-card">
                <div className="feature-icon-box mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon={f.icon} className="feature-icon h-6 w-6 text-[#2563eb]" />
                </div>
                <h3 className="feature-title mb-2 text-lg font-semibold text-[#1a365d]">{f.title}</h3>
                <p className="feature-desc text-sm text-[#4a5568]">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== LEADERSHIP ==================== */}
      <section id="leadership" className="scroll-mt-16 bg-gray-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-bold text-[#1a365d] md:text-4xl">Leading by Example</h2>
            <p className="text-lg text-[#2563eb]">37 Years of Distinguished Service</p>
          </div>
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.8, ease: easeOut }}>
              <div className="mb-8 flex items-center gap-6">
                <div className="h-32 w-32 flex-shrink-0 overflow-hidden rounded-full border-4 border-[#1a365d] shadow-xl">
                  <img src="/images/davis-headshot.jpg" alt="Major General (Ret.) T. Glenn Davis" className="h-full w-full object-cover" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1a365d]">Major General (Ret.) T. Glenn Davis</h3>
                  <p className="text-lg text-[#2563eb]">Chief Executive Officer</p>
                </div>
              </div>
              <div className="mb-8 space-y-4 leading-relaxed text-[#4a5568]">
                <p>General Davis most recently served as Senior Advisor and Program Manager for the Unification of the Royal Saudi Air Force and Royal Saudi Air Defense Force (January 2024 to January 2025), where he and his team personally trained over 1,000 General and Field Grade Officers on the Ministry of Defense&apos;s Target Operating Model for Saudi Vision 2030.</p>
                <p>Prior to this role, General Davis built a distinguished 37-year career in the United States Air Force. Commissioned in 1984 as the top graduate of the University of South Carolina ROTC program, he commanded one maintenance squadron and five logistics and maintenance groups, serving as both Deputy Director of Logistics for a combatant command and as an Assistant Deputy Director for the Joint Chiefs of Staff. He deployed to Al Jaber Air Base, Kuwait, the day after the 9/11 attacks, and also supported operations Northern Watch, Southern Watch and Deny Flight.</p>
                <p>In industry he directed Pratt &amp; Whitney&apos;s Oklahoma City operations supporting the F117, F119 and F135 engines, and served as Chief Operating Officer of United Dynamics. He is Executive Director of the National Security and Government Institute at the University of South Carolina Upstate.</p>
              </div>
              <div className="space-y-3">
                {credentials.map((c) => (
                  <div key={c.text} className="flex items-center gap-4 rounded-lg bg-white px-4 py-3 shadow-sm">
                    <img src={c.logo} alt="" className="h-8 w-8 flex-shrink-0 object-contain" />
                    <span className="font-medium text-[#4a5568]">{c.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.8, ease: easeOut }} className="grid grid-cols-2 gap-4">
              <img src="/images/davis-saudi-2.jpeg" alt="General Davis with officers in Riyadh" className="col-span-2 h-64 w-full rounded-xl object-cover object-top shadow-lg" />
              <img src="/images/davis-saudi-3.jpeg" alt="General Davis presenting a training certificate" className="h-48 w-full rounded-xl object-cover shadow-lg" style={{ objectPosition: '50% 40%' }} />
              <img src="/images/davis-saudi-1.jpeg" alt="General Davis with a senior officer" className="h-48 w-full rounded-xl object-cover object-top shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== CONTACT ==================== */}
      <section id="contact" className="scroll-mt-16 py-20" style={{ background: 'linear-gradient(135deg, #1a365d 0%, #1e6091 100%)' }}>
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">Contact Us</h2>
          <p className="mb-10 text-lg leading-relaxed text-blue-100">
            To discuss education for your organization, reach out to General Davis directly.
          </p>
          <a href={MAILTO} className="btn-sweep inline-flex bg-[#2563eb] px-8 py-4 text-lg font-semibold">
            <Icon icon="mdi:email-outline" className="h-6 w-6" />
            Email General Davis
          </a>
          <p className="mt-6">
            <a href={MAILTO} className="text-lg font-semibold text-white underline underline-offset-4 hover:text-blue-100">{CONTACT_EMAIL}</a>
          </p>
        </div>
      </section>
    </div>
  );
}
